import React, { useEffect } from 'react';
import { BrowserRouter as Router, useNavigate } from 'react-router-dom';
import { ThemeProvider } from './theme/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { WishlistProvider } from './context/WishlistContext';
import { UserProvider } from './context/UserContext';
import { ProductProvider } from './context/ProductContext';
import { OrderProvider } from './context/OrderContext';
import { CommunityProvider } from './context/CommunityContext';
import { DashboardNotificationProvider } from './context/DashboardNotificationContext';
import AuthHandler from './context/AuthHandler';
import AppRoutes from './routes';
import { NavigationService } from './services/navigationService';
import { ErrorService, LoadingService, NotificationService } from './services/errorService';
import NotificationProvider from './components/common/NotificationProvider';
import LoadingOverlay from './components/common/LoadingOverlay';
import LoadingScreen from './components/common/LoadingScreen';
import { TransitionProvider } from './context/TransitionContext';

// Navigation initializer component
const NavigationInitializer: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    NavigationService.initialize(navigate);
  }, [navigate]);

  return null;
};

/**
 * Main App component that serves as the entry point for the Frontend Landing Page
 * Wraps the application with necessary providers for theming, authentication, and routing
 * 
 * @returns {JSX.Element} The root application component with theme, auth, and routing setup
 */
const AppContent: React.FC = () => {
  const { loading: authLoading } = useAuth();
  const [minTimeElapsed, setMinTimeElapsed] = React.useState(false);
  const [isFullyRendered, setIsFullyRendered] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => setMinTimeElapsed(true), 10000);
    return () => clearTimeout(timer);
  }, []);

  const isLoading = authLoading || !minTimeElapsed;

  return (
    <>
      {!isFullyRendered && <LoadingScreen loading={isLoading} onTransitionComplete={() => setIsFullyRendered(true)} />}
      {!isLoading && (
        <>
          <NavigationInitializer />
          <AuthHandler />
          <LoadingOverlay />
          <AppRoutes />
        </>
      )}
    </>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <UserProvider>
          <ProductProvider>
            <OrderProvider>
            <CartProvider>
              <WishlistProvider>
                <CommunityProvider>
                  <DashboardNotificationProvider>
                    <NotificationProvider>
                      <TransitionProvider>
                        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                          <AppContent />
                        </Router>
                      </TransitionProvider>
                    </NotificationProvider>
                  </DashboardNotificationProvider>
                </CommunityProvider>
              </WishlistProvider>
            </CartProvider>
            </OrderProvider>
          </ProductProvider>
        </UserProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
